import { useEffect, useMemo, useState } from 'react'
import { subscribeToPipeline, subscribeToTemplates } from '../repository/settingsRepository'
import { DEFAULT_OUTCOMES, DEFAULT_STAGES, DEFAULT_TEMPLATES } from '../defaults'
import type {
  EmailTemplate,
  PipelineOutcome,
  PipelineStage,
  TemplateKey,
} from '@/types/crm'

export interface CrmSettings {
  stages: PipelineStage[]
  outcomes: PipelineOutcome[]
  templates: Record<TemplateKey, EmailTemplate>
  /** Stages + outcomes in display order — what the filter chips iterate. */
  allStageDefs: (PipelineStage | PipelineOutcome)[]
  /** Stages kept out of the lead list unless explicitly asked for. */
  hiddenStageKeys: Set<string>
  /** Resolve a stage key to its definition, falling back to the first stage. */
  findStage: (key: string) => PipelineStage | PipelineOutcome
  loading: boolean
  error: string | null
}

/**
 * Live pipeline + template settings.
 *
 * Both subscriptions fall back to the built-in defaults, so the CRM renders
 * correctly against a project where `skidaCrmSettings` has not been seeded yet.
 */
export function useCrmSettings(): CrmSettings {
  const [stages, setStages] = useState<PipelineStage[]>(DEFAULT_STAGES)
  const [outcomes, setOutcomes] = useState<PipelineOutcome[]>(DEFAULT_OUTCOMES)
  const [templates, setTemplates] =
    useState<Record<TemplateKey, EmailTemplate>>(DEFAULT_TEMPLATES)
  const [pipelineLoaded, setPipelineLoaded] = useState(false)
  const [templatesLoaded, setTemplatesLoaded] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    return subscribeToPipeline(
      (settings) => {
        setStages(settings.stages)
        setOutcomes(settings.outcomes)
        setPipelineLoaded(true)
      },
      (err) => {
        setError(err.message)
        setPipelineLoaded(true)
      },
    )
  }, [])

  useEffect(() => {
    return subscribeToTemplates(
      (settings) => {
        setTemplates(settings.templates)
        setTemplatesLoaded(true)
      },
      (err) => {
        setError(err.message)
        setTemplatesLoaded(true)
      },
    )
  }, [])

  return useMemo(() => {
    const allStageDefs = [...stages, ...outcomes]
    return {
      stages,
      outcomes,
      templates,
      allStageDefs,
      hiddenStageKeys: new Set(stages.filter((s) => s.hidden).map((s) => s.key)),
      findStage: (key: string) => allStageDefs.find((s) => s.key === key) ?? stages[0],
      loading: !pipelineLoaded || !templatesLoaded,
      error,
    }
  }, [stages, outcomes, templates, pipelineLoaded, templatesLoaded, error])
}
